import { useEffect, useRef, useCallback } from 'react';
import { useLocation } from 'react-router-dom';

const API = process.env.REACT_APP_API_URL;

// A visit (session) ends after 30 minutes without activity, like most analytics tools.
// The visitor id lives for good, so returning visitors can be told apart from new ones.
const SESSION_IDLE_MS = 30 * 60 * 1000;
const getVisitorId = () => {
  try {
    let v = localStorage.getItem('brandster_visitor_id');
    if (!v) {
      // Browsers that already had a long-lived session id keep it as their visitor id
      v = localStorage.getItem('brandster_analytics_session') || ('bv_' + Math.random().toString(36).substring(2) + Date.now().toString(36));
      localStorage.setItem('brandster_visitor_id', v);
    }
    return v;
  } catch (e) { return null; }
};
const getSessionId = () => {
  try {
    const now = Date.now();
    const last = parseInt(localStorage.getItem('brandster_analytics_last') || '0', 10);
    let sid = sessionStorage.getItem('brandster_visit_id') || localStorage.getItem('brandster_visit_id');
    if (!sid || !last || now - last > SESSION_IDLE_MS) {
      sid = 'bs_' + Math.random().toString(36).substring(2) + now.toString(36);
      localStorage.setItem('brandster_visit_id', sid);
    }
    sessionStorage.setItem('brandster_visit_id', sid);
    localStorage.setItem('brandster_analytics_last', String(now));
    return sid;
  } catch (e) {
    return 'bs_' + Math.random().toString(36).substring(2) + Date.now().toString(36);
  }
};
// Lets the server tell tablets apart (iPads report themselves as Macs) and record screen size
const getDeviceHint = () => ({
  screenWidth: window.innerWidth,
  touchPoints: navigator.maxTouchPoints || 0,
  platform: navigator.platform || '',
});

const getUTMParams = () => {
  const params = new URLSearchParams(window.location.search);
  return {
    utmSource: params.get('utm_source'),
    utmMedium: params.get('utm_medium'),
    utmCampaign: params.get('utm_campaign'),
    utmContent: params.get('utm_content'),
    utmTerm: params.get('utm_term'),
    refCode: params.get('ref'),
  };
};

// Remember the visitor's last campaign touch so an order placed later can be attributed to it
export const ATTRIBUTION_KEY = 'brandster_attribution';
export const getAttribution = () => {
  try {
    const a = JSON.parse(localStorage.getItem(ATTRIBUTION_KEY) || 'null');
    if (!a || Date.now() - a.t > 30 * 24 * 3600 * 1000) return null;
    return a;
  } catch (e) { return null; }
};

export const useAnalytics = (userId = null) => {
  const location = useLocation();
  const sessionId = getSessionId();
  const visitorId = getVisitorId();
  const startedSession = useRef(null);
  const pageStartTime = useRef(Date.now());
  const lastPage = useRef(null);
  const sessionStarted = useRef(false);

  // Start session on first load
  useEffect(() => {
    if (sessionStarted.current) return;
    sessionStarted.current = true;
    const utms = getUTMParams();
    try {
      if (utms.utmSource || utms.refCode) {
        localStorage.setItem(ATTRIBUTION_KEY, JSON.stringify({
          utm_source: utms.utmSource, utm_medium: utms.utmMedium, utm_campaign: utms.utmCampaign,
          ref: utms.refCode, t: Date.now(),
        }));
        // One 'campaign_visit' event per browser tab session and link, so refreshes are not counted twice
        const flag = 'bp_campaign_' + (utms.refCode || utms.utmCampaign || utms.utmSource);
        if (!sessionStorage.getItem(flag)) {
          sessionStorage.setItem(flag, '1');
          fetch(`${API}/api/analytics/event`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              sessionId, userId, eventType: 'campaign_visit',
              eventName: utms.refCode || utms.utmCampaign || utms.utmSource,
              page: location.pathname,
              properties: { source: utms.utmSource, medium: utms.utmMedium, campaign: utms.utmCampaign, content: utms.utmContent, term: utms.utmTerm },
            }),
          }).catch(() => {});
        }
      }
    } catch (e) { /* storage unavailable */ }
    fetch(`${API}/api/analytics/session`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        sessionId,
        userId,
        entryPage: location.pathname,
        referrer: document.referrer || null,
        visitorId, device: getDeviceHint(),
        ...utms,
      }),
    }).catch(() => {});
    startedSession.current = sessionId;
  }, []);

  // Update session when user logs in
  useEffect(() => {
    if (!userId) return;
    fetch(`${API}/api/analytics/session`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ sessionId, userId }),
    }).catch(() => {});
  }, [userId]);

  // Track pageview on route change
  useEffect(() => {
    const currentPage = location.pathname;

    // The visit id rotates after 30 idle minutes: open a fresh session record for it
    if (startedSession.current && startedSession.current !== sessionId) {
      startedSession.current = sessionId;
      fetch(`${API}/api/analytics/session`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ sessionId, userId, entryPage: currentPage, referrer: null, visitorId, device: getDeviceHint(), ...getUTMParams() }),
      }).catch(() => {});
    }

    // Send duration for previous page
    if (lastPage.current) {
      const duration = Math.round((Date.now() - pageStartTime.current) / 1000);
      fetch(`${API}/api/analytics/pageview`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          sessionId, userId,
          page: lastPage.current,
          title: document.title,
          duration,
          referrer: document.referrer || null,
        }),
      }).catch(() => {});
    }

    // Start tracking new page
    pageStartTime.current = Date.now();
    lastPage.current = currentPage;

    // Track new pageview
    fetch(`${API}/api/analytics/pageview`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        sessionId, userId,
        page: currentPage,
        title: document.title,
        referrer: document.referrer || null,
      }),
    }).catch(() => {});

  }, [location.pathname]);

  // Track event function
  const track = useCallback((eventType, eventName, properties = {}) => {
    fetch(`${API}/api/analytics/event`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        sessionId, userId,
        eventType, eventName,
        page: location.pathname,
        properties,
      }),
    }).catch(() => {});
  }, [sessionId, userId, location.pathname]);

  return { track, sessionId };
};